// Central place to define application-specific terminology

export const terminology = {
  client: { // This now refers to the original 'Client', which is 'Supplier'
    singular: "Supplier",
    plural: "Suppliers",
    // Add other related terms if needed, e.g., clientId: "Supplier ID"
  },
  household: { // This now refers to the original 'Household', which is 'Client'
    singular: "Client",
    plural: "Clients",
    // Add other related terms if needed
  },
  // Add other entities here as needed, e.g., franchisee
};

// Allocation type options - single source of truth
// export const allocationTypes = [
//   { value: 'monthly', label: 'Monthly' },
//   { value: 'weekly', label: 'Weekly' },
//   { value: 'fortnightly', label: 'Fortnightly' },
//   { value: 'fixed-fee', label: 'Set Fee' }
// ];
export const allocationTypes = [
  { value: 'fixed-fee', label: 'Set Fee' },
  { value: 'fixed-hours', label: 'Fixed Hours' }
];

// Frequency options (for recurring schedules)
export const allocationOptions = [
  { value: 'interval', label: 'Interval' },
  { value: 'monthly', label: 'Monthly' }
];

// Frequency interval units (when interval option is selected)
export const allocationIntervalUnits = [
  { value: 'Days', label: 'Days' },
  { value: 'Weeks', label: 'Weeks' }
];

// Schedule type options
export const scheduleTypes = [
  { value: 'one-off', label: 'One-off' },
  { value: 'recurring', label: 'Recurring' }
];

// Frequency options (for recurring schedules)
export const frequencyOptions = [
  { value: 'interval', label: 'Interval' },
  { value: 'monthly', label: 'Monthly' }
];

// Frequency interval units (when interval option is selected)
export const frequencyIntervalUnits = [
  { value: 'Days', label: 'Days' },
  { value: 'Weeks', label: 'Weeks' }
];

// Job status options
export const jobStatuses = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

// Quote status options
export const quoteStatuses = [
  { value: '', label: 'None' },
  { value: 'pending-quote', label: 'Quote Pending' },
  { value: 'quote-received', label: 'Quote Received' },
  { value: 'pending-approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'not-approved', label: 'Not Approved' },
];

// Utility functions for formatting display values
export const formatters = {
  // Format allocation type for display
  formatAllocationType: (type, value = null) => {
    if (!type) return 'N/A';

    // Handle interval-based allocations
    if (type === 'days' && value) {
      return value === 1 ? 'Daily' : `Every ${value} days`;
    }
    if (type === 'weeks' && value) {
      return value === 1 ? 'Weekly' : `Every ${value} weeks`;
    }

    // Handle existing allocations from the allocationTypes array
    const allocationType = allocationTypes.find(at => at.value === type);
    return allocationType ? allocationType.label : type.charAt(0).toUpperCase() + type.slice(1);
  },

  // Format schedule type for display
  formatScheduleType: (type) => {
    const scheduleType = scheduleTypes.find(st => st.value === type);
    return scheduleType ? scheduleType.label : type.replace('-', ' ');
  },

  // Format quote status for display
  formatQuoteStatus: (status) => {
    if (!status) return '';
    const quoteStatus = quoteStatuses.find(qs => qs.value === status);
    return quoteStatus ? quoteStatus.label : status;
  },

  // Format frequency for display (handles the complex frequency logic)
  formatFrequency: (type, value) => {
    if (!type) return 'N/A';
    const lowerType = type.toLowerCase();
    switch (lowerType) {
      case 'monthly':
        return 'Monthly';
      case 'days':
        return `Every ${value} ${Number(value) === 1 ? 'Day' : 'Days'}`;
      case 'weeks':
        return `Every ${value} ${Number(value) === 1 ? 'Week' : 'Weeks'}`;
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  },

  // Format allocation details combining hours and frequency into a single string
  // Similar to the email notification format
  formatAllocationDetails: (job) => {
    if (!job) return 'N/A';
    
    const allocationType = job.allocationType;
    const hours = Number(job.allotedHours) || 0;
    const additionalPrice = job.additionalPrice || job.invoicePricePerHour || 0;
    const allocationValue = Number(job.allocationValue) || 1;
    const frequencyType = typeof job.frequencyType === 'string' ? job.frequencyType.toLowerCase() : null;
    const frequencyValue = Number(job.frequencyValue) || 1;
    
    if (allocationType === 'monthly') {
      // Monthly allocation: show hours per month
      return `${hours} hours per month`;
    } else if (allocationType === 'days' || allocationType === 'weeks') {
      // For recurring jobs, prefer recurrence frequency if available to avoid
      // mismatches between schedule frequency and allocation display wording.
      const intervalType = (job.scheduleType === 'recurring' && (frequencyType === 'days' || frequencyType === 'weeks'))
        ? frequencyType
        : allocationType;
      const intervalValue = (job.scheduleType === 'recurring' && (frequencyType === 'days' || frequencyType === 'weeks'))
        ? frequencyValue
        : allocationValue;
      const allocationUnit = intervalType === 'days'
        ? (intervalValue === 1 ? 'day' : 'days')
        : (intervalValue === 1 ? 'week' : 'weeks');
      return `${hours} hours every ${intervalValue} ${allocationUnit}`;
    } else if (allocationType === 'fixed-fee' || allocationType === 'set-fee') {
      // Fixed fee allocation
      const fixedFee = additionalPrice;
      return `Fixed fee: $${fixedFee.toFixed(2)} (inc. GST)`;
    } else {
      // Default to showing allotted hours if available
      return `${hours} hours`;
    }
  }
};
